import { useCallback, useState, useEffect } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
}

/**
 * Tour hook for Contact Source Create form
 * Uses custom tour-styles for consistent styling and keyboard navigation
 */
export const useContactSourceTour = () => {
    const [isActive, setIsActive] = useState(false)
    const [currentTourStep, setCurrentTourStep] = useState(0)

    const tourSteps: TourStep[] = [
        {
            selector: '#contact_source_name',
            title: 'Tên liên hệ *',
            description: 'Nhập họ tên đầy đủ của người liên hệ',
        },
        {
            selector: '#contact_source_position',
            title: 'Chức vụ',
            description: 'Nhập chức vụ/vị trí của người liên hệ trong công ty',
        },
        {
            selector: '#contact_source_phone',
            title: 'Số điện thoại',
            description: 'Nhập số điện thoại liên hệ',
        },
        {
            selector: '#contact_source_email',
            title: 'Email *',
            description:
                'Nhập địa chỉ email. Hệ thống sẽ kiểm tra email đã tồn tại chưa',
        },
        {
            selector: '#contact_source_company',
            title: 'Công ty',
            description: 'Nhập tên công ty của người liên hệ',
        },
        {
            selector: '#contact_source_website',
            title: 'Website',
            description: 'Nhập địa chỉ website công ty (nếu có)',
        },
        {
            selector: '#contact_source_profile',
            title: 'Profile',
            description:
                'Nhập link profile LinkedIn, Facebook hoặc mạng xã hội khác',
        },
        {
            selector: '#contact_source_industry',
            title: 'Ngành nghề',
            description: 'Chọn ngành nghề kinh doanh của công ty',
        },
        {
            selector: '#contact_source_channel',
            title: 'Kênh tiếp cận',
            description: 'Chọn kênh mà bạn tiếp cận được khách hàng này',
        },
        {
            selector: '#contact_source_interested_services',
            title: 'Dịch vụ quan tâm',
            description:
                'Chọn các dịch vụ mà khách hàng quan tâm (có thể chọn nhiều)',
        },
        {
            selector: '#contact_source_status',
            title: 'Trạng thái',
            description: 'Chọn trạng thái của nguồn liên hệ này',
        },
        {
            selector: '#contact_source_notes',
            title: 'Ghi chú',
            description: 'Thêm ghi chú về khách hàng nếu cần',
        },
        {
            selector: '#contact_source_create',
            title: 'Tạo liên hệ',
            description: 'Nhấn nút để tạo nguồn liên hệ marketing mới',
        },
    ]

    const showTourUI = (step: TourStep, stepIndex: number) => {
        injectTourStyles()
        cleanupTourElements()
        createTourOverlay()

        const element = document.querySelector(step.selector) as HTMLElement
        if (!element) {
            // Skip to next step if element not found
            if (stepIndex < tourSteps.length - 1) {
                goToStep(stepIndex + 1)
            } else {
                endTour()
            }
            return
        }

        element.classList.add('tour-highlight-element')
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })

        // Wait for scroll then add popover
        setTimeout(() => {
            const popover = createTourPopover({
                title: step.title,
                description: step.description,
                currentStep: stepIndex + 1,
                totalSteps: tourSteps.length,
                onPrev:
                    stepIndex > 0 ? () => goToStep(stepIndex - 1) : undefined,
                onNext: () => {
                    if (stepIndex === tourSteps.length - 1) {
                        endTour()
                    } else {
                        goToStep(stepIndex + 1)
                    }
                },
                onClose: endTour,
                isLastStep: stepIndex === tourSteps.length - 1,
            })

            document.body.appendChild(popover)
            positionPopover(popover, element)
        }, 300)
    }

    const goToStep = (stepIndex: number) => {
        const step = tourSteps[stepIndex]
        if (!step) return

        cleanupTourElements()
        showTourUI(step, stepIndex)
        setCurrentTourStep(stepIndex)
    }

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentTourStep(0)
    }, [])

    const startTour = useCallback(() => {
        setIsActive(true)
        setCurrentTourStep(0)

        // Scroll to top first
        window.scrollTo({ top: 0, behavior: 'smooth' })

        // Start tour after scroll
        setTimeout(() => {
            showTourUI(tourSteps[0], 0)
        }, 300)
    }, [])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupTourElements()
        }
    }, [])

    return { startTour, endTour, isActive, currentStep: currentTourStep }
}

export default useContactSourceTour
